import { Stack, Typography } from '@mui/material';
import React from 'react';
import type { DataTransformer } from '../shared/data-transformers';
import { colors } from '../shared/ui-extension';
import Link from 'next/link';

export type ItemProperty = ItemPropertyTableValue<any>;

interface ItemPropertyTableValue<T = unknown> {
  isDivider: boolean;
  link?: string;
  title?: string;
  value?: T;
  transformer?: DataTransformer<T>;
  after?: React.ReactNode | React.ReactNode[];
}

interface ItemPropertyTableProps {
  properties: ItemPropertyTableValue<any>[];
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Creates a type-safe property for ItemPropertyTable.
 * Ensures that the DataTransformer type matches the value type.
 *
 * @example
 * createProperty('Count', 5, dataTransformers.integer)
 * createProperty('Active', true, dataTransformers.boolean)
 * createProperty('Price', 19.99, dataTransformers.currency)
 */
export function useProperty<T>(
  title: string,
  value: T,
  transformer?: DataTransformer<T>,
  after?: React.ReactNode | React.ReactNode[]
): ItemPropertyTableValue<T> {
  return {
    title,
    value,
    transformer,
    isDivider: false,
    link: undefined,
    after,
  };
}
export function useDivider(): ItemProperty {
  return { isDivider: true };
}
export function usePropertyLink<T>(
  title: string,
  value: T,
  link: string,
  transformer?: DataTransformer<T>,
  after?: React.ReactNode | React.ReactNode[]
): ItemPropertyTableValue<T> {
  return { title, value, transformer, isDivider: false, link: link, after };
}

const baseCss = {
  display: 'flex',
};
const titleCss = {
  ...baseCss,
  justifyContent: 'flex-end',
};
const valueCss = {
  ...baseCss,
  justifyContent: 'flex-start',
};

/// An item property table that conforms
/// to the Figma design. Make sure to use
/// properties with the `createProperty`
/// function.
export default function ItemPropertyTable({
  properties,
  className,
  style,
}: ItemPropertyTableProps) {
  return (
    <div
      className={className}
      style={{
        paddingTop: '4px',
        paddingBottom: '4px',
        display: 'grid',
        alignContent: 'start',
        columnGap: '10px',
        rowGap: '2px',
        gridTemplateColumns: '1fr 1fr',
        width: '100%',
        ...style,
      }}
    >
      {properties.map((prop) => (
        <React.Fragment key={prop.title}>
          {!prop.isDivider && (
            <div style={titleCss}>
              <Typography textAlign={'right'}>{prop.title}</Typography>
            </div>
          )}
          {!prop.isDivider && (
            <Stack
              direction='row'
              useFlexGap
              alignItems='center'
              justifyContent='space-between'
            >
              {prop.link ? (
                <>
                  <Link href={prop.link}>
                    <TableCell prop={prop} />
                  </Link>
                  {prop.after}
                </>
              ) : (
                <>
                  <TableCell prop={prop} />
                  {prop.after}
                </>
              )}
            </Stack>
          )}
          {prop.isDivider && (
            <div
              style={{
                background: colors.darkGray,
                gridColumn: '1 / span 2',
                height: '1px',
                width: 'calc(100% - 30px)',
                marginLeft: '15px',
              }}
            ></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function TableCell({ prop }: { prop: ItemProperty }) {
  return (
    <div style={valueCss}>
      <Typography fontWeight={'bold'}>
        {prop.transformer ? prop.transformer(prop.value) : String(prop.value)}
      </Typography>
    </div>
  );
}
